<template>
  <v-container fluid class="pa-0">
    <v-text-field
      :label="$t('mqtt.meta.fields.publishTopic')"
      v-model="localValue.publishTopic"
      :hint="$t('mqtt.meta.fields.publishTopicHint')"
    />
    <v-container fluid v-if="nodeType == 2">
      <div class="d-flex justify-space-between align-center">
        <div>
          <h4 class="text-left">{{ $t("mqtt.topicCreateTitle") }}</h4>
          <p class="text-left text--disabled outlined" style="max-width: 80%">
            {{ $t("mqtt.topicDescription") }}
          </p>
        </div>
        <v-btn
          @click="openDialog"
          outlined
          color="primary"
          :disabled="localValue.brokerId == ''"
        >
          <v-icon>mdi-plus</v-icon> {{ $t("mqtt.topicAdd") }}
        </v-btn>
      </div>
      <div class="topic-container">
        <div class="topic-title-box"><h4>Topics</h4></div>
        <div class="topic-list">
          <div
            class="topic-item d-flex justify-space-between align-center"
            v-for="(topic, i) in localValue.topics"
            :key="topic.topic"
          >
            <h5 class="text-left">{{ topic.topic }}</h5>
            <div class="topic-actions">
              <v-btn @click="editTopic(i, topic)" color="primary" icon outlined>
                <v-icon>mdi-pen</v-icon>
              </v-btn>
            </div>
          </div>
        </div>
      </div>
    </v-container>

    <v-dialog v-model="dialog" max-width="100%" style="min-height: 500px">
      <v-card>
        <v-card-title>{{ $t("mqtt.createTopicTitle") }}</v-card-title>
        <v-card-text>
          <h5 class="text-bold">Node ID:</h5>
          <h5>{{ this.deveui }}</h5>
        </v-card-text>
        <v-progress-circular
          v-if="loadingDecoder"
          indeterminate
          color="primary"
          style="width: 100%"
          :size="60"
          :width="8"
        ></v-progress-circular>
        <v-card-text v-else>
          <v-container>
            <v-row>
              <v-col cols="12" md="6" sm="12">
                <v-text-field
                  v-model="topic"
                  :label="$t('mqtt.meta.fields.topic')"
                  class="px-2"
                />
                <mqtt-parsing-type v-model="parsingType" />
                <codemirror
                  ref="cm"
                  v-if="topic != null && topic != ''"
                  class="disabled"
                  v-model="decoder"
                  style="height: auto"
                  @focus="
                    () => {
                      if (decoder == decoderPlaceholder)
                        decoder = defaultDecoder;
                    }
                  "
                  :options="{
                    tabSize: 4,
                    mode: 'javascript',
                    theme: 'monokai',
                    lineNumbers: false,
                    line: false,
                  }"
                />
              </v-col>
              <v-col cols="12" md="6" sm="12">
                <v-text-field
                  v-model="testTopic"
                  :label="$t('mqtt.meta.fields.testTopic')"
                  class="px-2"
                />

                <v-text-field
                  v-model="payload"
                  :label="$t('mqtt.meta.fields.payloadTest')"
                  class="px-2"
                />

                <div class="d-flex justify-end align-center">
                  <v-btn @click="testDecoder">{{ $t("common.test") }}</v-btn>
                </div>

                <h4
                  v-if="testErrorResult != undefined"
                  class="error text-left pa-3"
                  style="color: white; border-radius: 5px"
                >
                  {{ testErrorResult }}
                </h4>

                <json-display
                  v-if="testResult !== undefined"
                  :value="testResult"
                  hide-copy
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-text v-if="!topicValid && !loadingDecoder">
          <h4
            class="error text-left pa-3"
            style="color: white; border-radius: 5px"
          >
            {{ $t("mqtt.topicAlreadyExists") }}
          </h4>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="closeDialog" color="primary" text>{{
            $t("common.cancel")
          }}</v-btn>
          <v-btn v-if="isEditTopic" @click="deleteTopic" color="error">{{
            $t("common.delete")
          }}</v-btn>
          <v-btn :disabled="!topicValid" @click="save" color="primary">{{
            $t("common.save")
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { codemirror } from "vue-codemirror";
import "codemirror/mode/javascript/javascript.js";
import "codemirror/lib/codemirror.css";
import "codemirror/theme/monokai.css";

import MqttParsingType from "./MqttParsingType.vue";
import JsonDisplay from "../common/JsonDisplay.vue";

import mqttRepository from "../../api/repositories/mqttRepository";

export default {
  components: { codemirror, MqttParsingType, JsonDisplay },
  props: {
    nodeType: {
      type: Number,
      default: 0,
    },

    deveui: {
      type: String,
    },

    broker: {
      type: String,
    },

    edit: {
      type: Boolean,
      default: false,
    },

    value: {
      type: Object,
      default: () => {
        return {
          publishTopic: "",
          brokerId: "",
          topics: [],
        };
      },
    },
  },

  data() {
    return {
      dialog: false,
      isEditTopic: false,
      loadingDecoder: false,
      topicIndex: null,
      topic: "",
      parsingType: 0,
      decoderPlaceholder: "function Decode(topic, payload) {}",
      defaultDecoder: `function Decode(topic, payload) {
  var json = JSON.parse(payload);

  return [
    {
      deveui: "deveui-to-add-value-to", 
      key: "temp", 
      value: json.temprature, 
      timestamp: new Date()
    }
  ];
}`,

      decoder: "",
      payload: "",
      testTopic: "",
      testErrorResult: undefined,
      testResult: undefined,
    };
  },

  computed: {
    localValue: {
      get() {
        return this.value;
      },

      set(val) {
        this.$emit("input", val);
      },
    },

    topicValid() {
      if (this.localValue.publishTopic == this.topic) return false;
      if (this.isEditTopic)
        return !this.localValue.topics
          .filter((e, i) => this.topicIndex != i)
          .map((d) => d.topic)
          .includes(this.topic);

      return !this.localValue.topics.map((d) => d.topic).includes(this.topic);
    },
  },

  methods: {
    editTopic(i, topic) {
      this.loadingDecoder = true;
      this.$nextTick(() => {
        this.isEditTopic = true;
        this.dialog = true;

        setTimeout(() => {
          this.topic = topic.topic;
          this.decoder = topic.decoder;
          this.parsingType = topic.parsingType;
          this.topicIndex = i;
          this.loadingDecoder = false;
        }, 400);
      });
    },

    openDialog() {
      this.loadingDecoder = true;
      this.$nextTick(() => {
        this.dialog = true;
        setTimeout(() => {
          this.topic = "";
          this.decoder = this.defaultDecoder;
          this.parsingType = 0;
          this.loadingDecoder = false;
        }, 400);
      });
    },

    closeDialog() {
      this.dialog = false;
      this.topicIndex = null;
      this.isEditTopic = false;

      this.$emit("input", this.localValue);
    },

    async testDecoder() {
      this.testErrorResult = undefined;
      this.testResult = undefined;

      let pl = this.payload;
      if (typeof pl !== "string") pl = JSON.stringify(pl);

      let result = await mqttRepository.testDecode({
        topic: this.testTopic,
        decoder: this.decoder,
        payload: pl,
        parsingType: this.parsingType,
      });

      if (!result.success) this.testErrorResult = result.response;
      else this.testResult = JSON.stringify(result.response);
    },

    async save() {
      if (this.isEditTopic && this.topicIndex != null)
        this.localValue.topics[this.topicIndex] = {
          topic: this.topic,
          parsingType: this.parsingType,
          decoder: this.decoder,
        };
      else {
        this.localValue.topics.push({
          topic: this.topic,
          parsingType: this.parsingType,
          decoder: this.decoder,
        });

        this.topic = "";
        this.decoder = this.defaultDecoder;
      }

      this.$emit("input", this.localValue);
      this.dialog = false;
      this.isEditTopic = false;

      this.decoder = this.decoderPlaceholder;
      this.topic = "";
    },

    deleteTopic() {
      this.dialog = false;
      if (this.topicIndex != null)
        this.localValue.topics.splice(this.topicIndex, 1);

      this.$emit("input", this.localValue);
    },
  },

  watch: {
    broker(val) {
      this.localValue.brokerId = val;
    },

    deveui(val) {
      this.localValue.deveui = val;
    },
  },

  created() {
    if (this.deveui != null && this.deveui != "")
      this.localValue.deveui = this.deveui;
    if (this.broker != null && this.broker != "")
      this.localValue.brokerId = this.broker;

    if (this.localValue.decoder != null && this.localValue.decoder != "")
      this.decoder = this.localValue.decoder;
    else this.decoder = this.decoderPlaceholder;
  },
};
</script>

<style lang="scss">
.topic-container {
  width: 100%;
  border: 2px solid #e9e9e9;

  .topic-title-box {
    width: 100%;
    background: #e9e9e9;
    color: black;
    padding: 0.7rem;
    text-align: left;
  }

  .topic-list {
    min-height: 140px;
    width: 100%;

    .topic-item {
      background: white;
      color: black;
      border-bottom: 3px solid #e9e9e9;
      padding: 1rem;
      width: 100%;
    }
  }
}
</style>
